import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { Interactive } from "../src/interactions/Interactive";

const INTERACTION_RADIUS = 6;
const LOOK_LERP = 0.18;
const HUD_MESSAGE = "Parece haber algo en la cima de este montón de piezas convenientemente acomodadas para escalarlas.";

export default function PopCat({
  position = [-12.29, -0.08, -6.37],
  rotation = [0, 0, 0],
  scale = 1,
  targetRef = null,
  ...props
}) {
  const group = useRef();
  const interactiveRef = useRef(null);
  const { scene, animations } = useGLTF("/pop_cat.glb");
  const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { actions } = useAnimations(animations, group);

  const baseEuler = useMemo(
    () => new THREE.Euler(rotation[0], rotation[1], rotation[2]),
    [rotation]
  );
  const worldPosition = useMemo(() => new THREE.Vector3(), []);
  const targetPosition = useMemo(() => new THREE.Vector3(), []);
  const modelBounds = useMemo(() => new THREE.Box3(), []);
  const modelCenter = useMemo(() => new THREE.Vector3(), []);

  const lerpAngle = useCallback((current, target, alpha) => {
    let diff = ((target - current + Math.PI) % (Math.PI * 2)) - Math.PI;
    if (diff < -Math.PI) diff += Math.PI * 2;
    return current + diff * alpha;
  }, []);

  const interactive = useMemo(() => {
    const instance = new Interactive({
      id: "pop-cat",
      promptText: "Inspeccionar [E]",
      hudText: HUD_MESSAGE,
      radius: INTERACTION_RADIUS,
      cameraConfig: {
        position: [-5.81, -0.12, -15],
        transitionDuration: 1,
      },
      getCameraTarget: () => {
        if (!group.current) return null;
        modelBounds.setFromObject(group.current);
        modelBounds.getCenter(modelCenter);
        return [modelCenter.x, modelCenter.y, modelCenter.z];
      },
    });

    interactiveRef.current = instance;
    return instance;
  }, [modelBounds, modelCenter]);

  useEffect(() => () => {
    interactiveRef.current = null;
    interactive.dispose();
  }, [interactive]);

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child.isMesh || child.isSkinnedMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [clonedScene]);

  useEffect(() => {
    if (group.current) {
      group.current.rotation.set(baseEuler.x, baseEuler.y, baseEuler.z);
    }
  }, [baseEuler]);

  useEffect(() => {
    if (!actions) return undefined;

    const actionList = Object.values(actions);
    if (actionList.length === 0) return undefined;

    const [primaryAction] = actionList;
    if (!primaryAction) return undefined;

    primaryAction.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.2).play();
    primaryAction.clampWhenFinished = false;

    return () => {
      primaryAction.stop();
    };
  }, [actions]);

  useFrame((_, delta) => {
    const popCatGroup = group.current;
    const interactiveInstance = interactiveRef.current;
    if (!popCatGroup || !interactiveInstance) return;

    popCatGroup.rotation.x = baseEuler.x;
    popCatGroup.rotation.z = baseEuler.z;

    popCatGroup.getWorldPosition(worldPosition);

    const targetGroup = targetRef?.current?.group;
    if (!targetGroup || typeof targetGroup.translation !== "function") {
      interactiveInstance.setWithinRadius(false);
      const alpha = 1 - Math.pow(1 - LOOK_LERP, delta * 60);
      popCatGroup.rotation.y = lerpAngle(popCatGroup.rotation.y, baseEuler.y, alpha);
      return;
    }

    const translation = targetGroup.translation();
    if (!translation) {
      interactiveInstance.setWithinRadius(false);
      const alpha = 1 - Math.pow(1 - LOOK_LERP, delta * 60);
      popCatGroup.rotation.y = lerpAngle(popCatGroup.rotation.y, baseEuler.y, alpha);
      return;
    }

    targetPosition.set(translation.x, translation.y, translation.z);
    const distance = worldPosition.distanceTo(targetPosition);
    interactiveInstance.setWithinRadius(distance <= INTERACTION_RADIUS, distance);

    const desiredYaw = baseEuler.y;
    const alpha = 1 - Math.pow(1 - LOOK_LERP, delta * 60);
    popCatGroup.rotation.y = lerpAngle(popCatGroup.rotation.y, desiredYaw, alpha);
  });

  return (
    <primitive
      ref={group}
      object={clonedScene}
      position={position}
      scale={scale}
      {...props}
    />
  );
}

useGLTF.preload("/pop_cat.glb");
